import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { normalizeModulesList, normalizeModuleName } from '@/utils/moduleNormalization';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Feature Config Context
 * Centralized state for modules, features, and UI configs
 * Fetched once on login, cached, and shared across all components
 */

const FeatureConfigContext = createContext(null);

export function FeatureConfigProvider({ children }) {
  const sessionMods = typeof window !== 'undefined'
    ? normalizeModulesList(window.__APP_SESSION__?.plan_modules || [])
    : [];
  const [modules, setModules] = useState(sessionMods);
  const [oncallEnabled, setOncallEnabled] = useState(false);
  const [features, setFeatures] = useState([]);
  const [profileCards, setProfileCards] = useState([]);
  const [settingsItems, setSettingsItems] = useState([]);
  const [notificationCategories, setNotificationCategories] = useState([]);
  const [loading, setLoading] = useState(sessionMods.length === 0);
  const [secondaryLoaded, setSecondaryLoaded] = useState(false);
  const [secondaryLoading, setSecondaryLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [error, setError] = useState(null);

  // Modules-only bootstrap (nav needs this). Settings/profile deferred.
  const fetchConfig = useCallback(async (force = false) => {
    if (!force && lastFetch && Date.now() - lastFetch < CACHE_DURATION) {
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setModules([]);
      setOncallEnabled(false);
      setFeatures([]);
      setProfileCards([]);
      setSettingsItems([]);
      setNotificationCategories([]);
      setSecondaryLoaded(false);
      setLoading(false);
      return;
    }

    const seeded = normalizeModulesList(window.__APP_SESSION__?.plan_modules || []);
    if (seeded.length > 0 && !force) {
      setModules(seeded);
      setLoading(false);
    }

    try {
      if (!seeded.length) setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const modulesRes = await fetch(`${BACKEND_URL}/api/auth/me/modules`, { headers });

      if (modulesRes.ok) {
        const data = await modulesRes.json();
        setModules(normalizeModulesList(data.modules || []));
        setOncallEnabled(!!data.oncall_enabled);
        setLastFetch(Date.now());
        setError(null);
      } else {
        console.error('Failed to fetch modules:', modulesRes.status);
        setError(`modules fetch failed (${modulesRes.status})`);
      }
    } catch (err) {
      console.error('Error fetching feature config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [lastFetch]);

  const ensureSecondaryConfig = useCallback(async (force = false) => {
    if (secondaryLoaded && !force) return;
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      setSecondaryLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [profileCardsRes, settingsRes, notifCatsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/features/config/profile-cards`, { headers }),
        fetch(`${BACKEND_URL}/api/features/config/settings-items`, { headers }),
        fetch(`${BACKEND_URL}/api/features/config/notification-categories`, { headers }),
      ]);

      if (profileCardsRes.ok) {
        const data = await profileCardsRes.json();
        setProfileCards(data.cards || []);
        setFeatures(data.available_features || []);
      }
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettingsItems(data.settings || []);
      }
      if (notifCatsRes.ok) {
        const data = await notifCatsRes.json();
        setNotificationCategories(data.categories || []);
      }
      setSecondaryLoaded(true);
    } catch (err) {
      console.error('Error fetching secondary feature config:', err);
    } finally {
      setSecondaryLoading(false);
    }
  }, [secondaryLoaded]);

  const clearConfig = useCallback(() => {
    setModules([]);
    setOncallEnabled(false);
    setFeatures([]);
    setProfileCards([]);
    setSettingsItems([]);
    setNotificationCategories([]);
    setSecondaryLoaded(false);
    setLastFetch(null);
    setLoading(false);
  }, []);

  const refreshConfig = useCallback(async () => {
    await fetchConfig(true);
    if (secondaryLoaded) await ensureSecondaryConfig(true);
  }, [fetchConfig, ensureSecondaryConfig, secondaryLoaded]);

  useEffect(() => {
    fetchConfig();

    const handleLogin = () => {
      fetchConfig(true);
    };
    const handleSessionReady = () => {
      fetchConfig(false);
    };
    const handleLogout = () => {
      clearConfig();
    };

    window.addEventListener('user-login', handleLogin);
    window.addEventListener('user-session-ready', handleSessionReady);
    window.addEventListener('user-logout', handleLogout);

    return () => {
      window.removeEventListener('user-login', handleLogin);
      window.removeEventListener('user-session-ready', handleSessionReady);
      window.removeEventListener('user-logout', handleLogout);
    };
  }, [fetchConfig, clearConfig]);

  // Helper functions
  const hasModule = useCallback((moduleName) => {
    const normalized = normalizeModuleName(moduleName);
    return modules.includes(normalized) || modules.includes(moduleName);
  }, [modules]);

  const hasFeature = useCallback((featureKey) => {
    return features.includes(featureKey);
  }, [features]);

  const shouldShowCard = useCallback((cardKey) => {
    return profileCards.some(card => card.card_key === cardKey);
  }, [profileCards]);

  const getCard = useCallback((cardKey) => {
    return profileCards.find(card => card.card_key === cardKey);
  }, [profileCards]);

  const hasSettingAccess = useCallback((path) => {
    return settingsItems.some(item => item.path === path);
  }, [settingsItems]);

  const hasNotificationCategory = useCallback((category) => {
    return notificationCategories.includes(category);
  }, [notificationCategories]);

  const value = useMemo(() => ({
    modules,
    oncallEnabled,
    features,
    profileCards,
    settingsItems,
    notificationCategories,
    loading,
    secondaryLoading,
    error,
    refreshConfig,
    clearConfig,
    ensureSecondaryConfig,
    hasModule,
    hasFeature,
    shouldShowCard,
    getCard,
    hasSettingAccess,
    hasNotificationCategory
  }), [
    modules,
    oncallEnabled,
    features,
    profileCards,
    settingsItems,
    notificationCategories,
    loading,
    secondaryLoading,
    error,
    refreshConfig,
    clearConfig,
    ensureSecondaryConfig,
    hasModule,
    hasFeature,
    shouldShowCard,
    getCard,
    hasSettingAccess,
    hasNotificationCategory
  ]);

  return (
    <FeatureConfigContext.Provider value={value}>
      {children}
    </FeatureConfigContext.Provider>
  );
}

/**
 * Hook to access feature config
 */
export function useFeatureConfig() {
  const context = useContext(FeatureConfigContext);
  if (!context) {
    throw new Error('useFeatureConfig must be used within a FeatureConfigProvider');
  }
  return context;
}

/**
 * Legacy hook for backwards compatibility
 * Components using useAvailableModules will now use cached data
 */
export function useAvailableModules() {
  const { modules, loading, oncallEnabled } = useFeatureConfig();
  return { modules, loading, oncallEnabled };
}

/**
 * Hook for profile cards (cached)
 */
export function useProfileCards() {
  const { profileCards, features, shouldShowCard, getCard, loading, secondaryLoading, error, ensureSecondaryConfig } = useFeatureConfig();
  useEffect(() => {
    ensureSecondaryConfig();
  }, [ensureSecondaryConfig]);
  return {
    cards: profileCards,
    availableFeatures: features,
    hasFeature: (key) => features.includes(key),
    shouldShowCard,
    getCard,
    loading: loading || secondaryLoading,
    error
  };
}

/**
 * Hook for settings items (cached)
 */
export function useSettingsItems() {
  const { settingsItems, features, hasSettingAccess, loading, secondaryLoading, error, ensureSecondaryConfig } = useFeatureConfig();

  useEffect(() => {
    ensureSecondaryConfig();
  }, [ensureSecondaryConfig]);
  
  const getSettingsByCategory = useCallback(() => {
    const grouped = {};
    for (const item of settingsItems) {
      const category = item.category || 'Other';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(item);
    }
    return grouped;
  }, [settingsItems]);

  return {
    settings: settingsItems,
    availableFeatures: features,
    hasFeature: (key) => features.includes(key),
    hasSettingAccess,
    getSettingsByCategory,
    loading: loading || secondaryLoading,
    error
  };
}

/**
 * Hook for notification categories (cached)
 */
export function useNotificationCategories() {
  const {
    notificationCategories,
    features,
    hasNotificationCategory,
    loading,
    secondaryLoading,
    error,
    ensureSecondaryConfig,
  } = useFeatureConfig();
  useEffect(() => {
    ensureSecondaryConfig();
  }, [ensureSecondaryConfig]);
  return {
    categories: notificationCategories,
    availableFeatures: features,
    hasCategory: hasNotificationCategory,
    loading: loading || secondaryLoading,
    error,
  };
}
